import React from 'react';
import ProductPage from '../components/ProductPage';
import { AmmTaskFinder } from '../components/AmmTaskFinder';
import { getBoeingCoverage } from '../data/acousticCameras';

const FOTRIC_IMAGES = [
  'https://static.wixstatic.com/media/571718_b5167ab45dba4ec687063bb3e2d9a5cc~mv2.png/v1/fill/w_720,h_530,al_c,q_85,enc_auto/571718_b5167ab45dba4ec687063bb3e2d9a5cc~mv2.png',
  'https://static.wixstatic.com/media/571718_793d869993f54e72b0c1725b8e217fcb~mv2.png/v1/fill/w_720,h_534,al_c,q_85,enc_auto/571718_793d869993f54e72b0c1725b8e217fcb~mv2.png',
  'https://static.wixstatic.com/media/571718_48dbcde433584751a6c6c5bc02c19ca7~mv2.png/v1/fill/w_720,h_585,al_c,q_85,enc_auto/571718_48dbcde433584751a6c6c5bc02c19ca7~mv2.png',
];

const AcousticCameraHFlexPage = ({ onRequestDemo, onRequestQuote }) => {
  const productData = {
    productName: 'FOTRIC H-Flex Acoustic Imaging Camera',
    tagline: 'Where Flexibility Meets Precision — 180° Rotatable Sensor Array',
    description:
      'The FOTRIC H-Flex (H6Flex) is the industry\'s first acoustic imaging camera with a fully rotatable 180° sensor array. It lets technicians inspect overhead, ground-level, and hard-to-reach aviation equipment while keeping their arms low and their back straight — reducing shoulder and lumbar strain and, on energized ground-support gear, keeping the operator outside the arc-flash hazard zone. Behind the ergonomics is the same 162 MEMS digital microphone array as the H-Series, paired with FOTRIC\'s NaviPdM® AI inspection assistant for leak evaluation and partial discharge diagnostics.',
    image: FOTRIC_IMAGES[0],
    images: FOTRIC_IMAGES,
    imageContain: true,
    features: [
      {
        title: '180° Rotatable Sensor Array',
        description:
          'Stepless full-range rotation lets the acoustic array tilt independently of the display — overhead transmission lines, high engine cowls, ceiling ducting, and low-mounted GPU cabinets are all inspectable from a natural, safe posture.',
      },
      {
        title: 'Ergonomic Overhead Inspection',
        description:
          'Reduces the sustained arm elevation that drives shoulder injury risk. Traditional acoustic cameras force 90° arm-up posture; H-Flex keeps arms at a safer ~30° while capturing the same overhead target.',
      },
      {
        title: 'Ergonomic Ground-Level Inspection',
        description:
          'Inspect distribution cabinets, motor bases, and pipeline undersides while standing upright — no bending, kneeling, or squatting that strains the lumbar spine over a shift.',
      },
      {
        title: 'Arc Flash Safe Distance',
        description:
          'Enables inspection of energized components from >90 cm away, keeping operators outside the arc-flash hazard zone defined by NFPA 70E and IEEE 1584 — often eliminating the need for heavy-duty PPE.',
      },
      {
        title: '162 MEMS Digital Microphones',
        description:
          'High-density array delivers the same sensitivity as the H-Series — catches minor leaks and partial discharges long before they escalate into failures.',
      },
      {
        title: 'NaviPdM® AI Inspection Assistant',
        description:
          'Approximately 90% of inspector time is spent on repetitive logistical tasks. NaviPdM® automates asset tagging, data capture, and report packaging so technicians focus on judgment, not paperwork.',
      },
      {
        title: 'Acoustic Image Delay (T-FFTD®)',
        description:
          'Multi-modal transient acoustic matrix enhances microsecond-level event retention so intermittent sounds stay visible long enough to diagnose.',
      },
      {
        title: 'Leak Evaluation Mode',
        description:
          'Instantly reflects the annual energy cost of a detected leak and generates a shareable report — prioritize repairs by financial impact, not guesswork.',
      },
      {
        title: 'Partial Discharge Diagnostics',
        description:
          'Automatically identifies surface, floating, and corona discharges with severity assessment — critical for transformers, switchgear, and GSE electrical inspections.',
      },
      {
        title: 'Gray-Scale Background Mode',
        description:
          'Renders the optical image in industrial gray-scale so the acoustic overlay stands out even in visually busy environments.',
      },
      {
        title: 'On-Device Word/PDF Reports',
        description:
          'Generate finished Word or PDF inspection reports directly on the camera — no laptop hand-off, no export delay.',
      },
      {
        title: 'Field-Replaceable High-Capacity Battery',
        description:
          '3.6 V, 9900 mAh rechargeable Li-ion battery delivers ≥4 hours of continuous operation per pack.',
      },
    ],
    specifications: [
      { label: 'Articulate Microphone Array', value: '180° rotatable, stepless' },
      { label: 'Microphone Channels', value: '162 MEMS digital microphones' },
      { label: 'Acoustic Image FOV', value: '66° × 52°' },
      { label: 'Operating Distance', value: '0.3 – 100 m' },
      { label: 'Detection Modes', value: 'Leakage mode; Partial Discharge (PRPD, 50/60 Hz)' },
      { label: 'NaviPdM® AI Assistant', value: 'Included' },
      { label: 'Acoustic Image Delay', value: 'T-FFTD® — captures instantaneous sound events' },
      { label: 'Acoustic Image Focus', value: 'Masks surrounding areas' },
      { label: 'On-Device Analysis', value: 'Direct analysis of acoustic images and holographic acoustic videos' },
      { label: 'On-Device Report Generation', value: 'Word or PDF' },
      { label: 'Partial Discharge Diagnosis', value: 'Surface, floating, corona — with severity assessment' },
      { label: 'Display', value: '5" 1280 × 720 IPS LCD, Gorilla anti-explosion touch cover' },
      { label: 'Image Modes', value: 'Single-source, multi-source, hologram' },
      { label: 'Digital Camera', value: '13 Megapixel industrial-grade' },
      { label: 'Storage Card', value: 'SD, hot-swappable, up to 1 TB' },
      { label: 'Battery', value: '3.6 V, 9900 mAh rechargeable Li-ion, field-replaceable' },
      { label: 'Battery Runtime', value: '≥ 4 hours continuous' },
    ],
    applications: [
      'Overhead engine cowl and pylon inspections without shoulder strain',
      'Ceiling-mounted hangar HVAC and duct leak checks',
      'Ground-level GPU / GSE cabinet inspection from a safe standing posture',
      'Substation and switchgear PD inspection at safe arc-flash distances',
      'Cabin pressurization walk-downs in cramped fuselage sections',
      'Under-wing hydraulic and fuel line acoustic surveys',
      'Landing gear well pneumatic leak checks from a natural posture',
      'Compressed air system audits across hangar utility rooms',
      'AOG response for hard-to-access acoustic faults',
      'Transformer and rectifier bay partial discharge monitoring',
    ],
    partNumber: 'FOTRIC-HFLEX',
    boeingToolTag: 'COM-18702 (equivalent)',
    boeingCoverage: getBoeingCoverage('h-flex'),
    compareCameraId: 'h-flex',
  };

  return <ProductPage whereToBuy {...productData} extraSections={<AmmTaskFinder tool="COM-18702" />} onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />;
};

export default AcousticCameraHFlexPage;
